import { useCallback, useEffect, useState } from 'react'
import { Download, FileText, Layers, Percent, Pencil, Plus, Search, ShieldPlus, SlidersHorizontal, Trash2, Users } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { NumberInput } from '@/components/ui/NumberInput'
import { RichTextEditor } from '@/components/ui/RichTextEditor'
import { RichHtml } from '@/components/ui/RichHtml'
import { richToPlain, wordCount } from '@/lib/richText'
import { Select } from '@/components/ui/Select'
import { Modal } from '@/components/ui/Modal'
import { FormSidemenu, type FormSection } from '@/components/ui/FormSidemenu'
import { Toggle } from '@/components/ui/Toggle'
import { StatusToggle } from '@/components/ui/StatusToggle'
import { FixedTable, RowIconButton, SectionHeader, TextPanel, TextToggle, type FixedColumn, type FixedGroup } from '@/components/ui/FixedTable'
import { useExpandedRows } from '@/hooks/useExpandedRows'
import { Pill, StatusPill } from '@/components/ui/StatusPill'
import { normalizeForMatch } from '@/lib/validators'
import { ProposalLinesService } from '@/services/modules/ProposalLinesService'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'
import { exportToXlsx, type ExportColumn } from '@/lib/exportXlsx'
import { useAuthority } from '@/hooks/useAuthority'
import { ProposalCatalogNav } from './ProposalCatalogNav'
import {
  OPTION_KIND_LABELS,
  ProposalSetupService as Service,
  type OptionKind,
  type ProposalBeneficiaryGroup,
  type ProposalCategory,
  type ProposalCoverageItem,
  type ProposalDiscountCurve,
  type ProposalOption,
  type ProposalTextBlock,
  type ProposalTier,
  type TextBlockKind,
} from '@/services/modules/ProposalSetupService'

type Tab = 'categories' | 'items' | 'groups' | 'texts' | 'options' | 'curves'

/**
 * Tipos de bloco de texto. O `where` diz onde o bloco sai no PDF — sem isso o
 * cadastro vira uma lista de caixas sem contexto nenhum.
 */
const TEXT_KINDS: { value: TextBlockKind; label: string; where: string }[] = [
  { value: 'CONDICOES',   label: 'Condições contratuais',      where: 'Vira a lista da seção "Condições contratuais" do PDF — uma linha por cláusula.' },
  { value: 'COBERTURAS',  label: 'Coberturas e benefícios',    where: 'Descreve o que o plano entrega. Normalmente é escrito por categoria.' },
  { value: 'MODALIDADES', label: 'Modalidades de adesão',      where: 'Explica quem pode aderir em cada tipologia.' },
  { value: 'NR01',        label: 'NR-01 (riscos psicossociais)', where: 'Bloco do programa de gestão de riscos psicossociais.' },
  { value: 'OBSERVACOES', label: 'Observações',                where: 'Notas comerciais impressas ao final do documento.' },
  { value: 'OUTROS',      label: 'Outros',                     where: 'Qualquer bloco que não se encaixe nos anteriores.' },
]

/** O que cada lista de seleção alimenta no formulário da proposta. */
const OPTION_KIND_HELP: Record<OptionKind, string> = {
  CONTRACT_TYPE:  'Compulsória, livre adesão, administrada. A descrição de cada uma vira cláusula no PDF.',
  MIN_MONTHS:     'Tempo de permanência mínima oferecido (12, 24, 36 meses).',
  PAYMENT_DAY:    'Quando a mensalidade vence.',
  FIRST_PAYMENT:  'Prazo do primeiro pagamento após o aceite.',
  PAYMENT_METHOD: 'Formas de pagamento oferecidas — a proposta marca uma ou várias.',
  SIGNING_CITY:   'Cidade que abre a assinatura ("São Paulo, 30 de agosto de 2026"). Não é a cidade do cliente.',
}

const OPTION_KINDS = Object.keys(OPTION_KIND_LABELS) as OptionKind[]

/**
 * Cadastros do módulo de propostas — categorias, tipologias, itens de
 * cobertura, grupos de beneficiários, textos do PDF e as opções selecionáveis
 * de vigência e pagamento.
 *
 * O módulo é independente do resto do sistema: nada aqui vem de planos,
 * parentescos ou serviços. Sem esta tela a proposta funciona mas não pode ser
 * ajustada sem deploy.
 */
export function SettingsProposalSetupPage() {
  const [tab, setTab] = useState<Tab>('categories')
  // Todos os cadastros (categorias, tipologias, grupos, itens, textos, opções,
  // curvas) usam as mesmas chaves: POST → create, PUT → edit, DELETE → delete.
  const { can } = useAuthority()
  const canCreate = can('settings.proposal_setup.create')
  const canEdit = can('settings.proposal_setup.edit')
  const canDelete = can('settings.proposal_setup.delete')

  const [categories, setCategories] = useState<ProposalCategory[]>([])
  const [items, setItems] = useState<ProposalCoverageItem[]>([])
  const [groups, setGroups] = useState<ProposalBeneficiaryGroup[]>([])
  const [texts, setTexts] = useState<ProposalTextBlock[]>([])
  const [options, setOptions] = useState<ProposalOption[]>([])
  const [curves, setCurves] = useState<ProposalDiscountCurve[]>([])
  const [loading, setLoading] = useState(true)

  const [categoryForm, setCategoryForm] = useState<Partial<ProposalCategory> | null>(null)
  const [tierForm, setTierForm] = useState<(Partial<ProposalTier> & { categoryId: string }) | null>(null)
  const [itemForm, setItemForm] = useState<Partial<ProposalCoverageItem> | null>(null)
  const [groupForm, setGroupForm] = useState<Partial<ProposalBeneficiaryGroup> | null>(null)
  const [textForm, setTextForm] = useState<Partial<ProposalTextBlock> | null>(null)
  const [optionForm, setOptionForm] = useState<Partial<ProposalOption> | null>(null)
  const [curveForm, setCurveForm] = useState<Partial<ProposalDiscountCurve> | null>(null)
  const [removing, setRemoving] = useState<{ kind: string; id: string; name: string } | null>(null)
  const [busy, setBusy] = useState(false)

  // Busca e filtros (só na tela; nada vai ao servidor).
  const [catQuery, setCatQuery] = useState('')
  const [itemQuery, setItemQuery] = useState('')
  const [itemStatus, setItemStatus] = useState<'all' | 'on' | 'off'>('all')
  const [optionKind, setOptionKind] = useState<OptionKind>(OPTION_KINDS[0])
  // Descrições longas abrem na própria linha (nada de texto corrido na tabela).
  const exp = useExpandedRows()
  // Em que linhas cada cobertura entra (vem do catálogo de linhas).
  const [linesByItem, setLinesByItem] = useState<Record<string, string[]>>({})

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [c, i, g, t, o, cu, ln] = await Promise.all([
        Service.categories().catch(() => []),
        Service.items().catch(() => []),
        Service.groups().catch(() => []),
        Service.textBlocks().catch(() => []),
        Service.options().catch(() => []),
        Service.curves().catch(() => []),
        ProposalLinesService.list().then(r => r.data).catch(() => []),
      ])
      setCategories(c); setItems(i); setGroups(g); setTexts(t); setOptions(o); setCurves(cu)
      const porItem: Record<string, string[]> = {}
      for (const line of ln) for (const li of line.items) (porItem[li.itemId] ??= []).push(line.name)
      setLinesByItem(porItem)
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { void load() }, [load])

  async function save(fn: () => Promise<unknown>, close: () => void) {
    setBusy(true)
    try {
      await fn()
      toast.success({ title: 'Salvo', description: 'Cadastro atualizado.' })
      close(); await load()
    } catch (e) {
      toast.error({ title: 'Não foi possível salvar', description: extractApiError(e).message })
    } finally { setBusy(false) }
  }

  async function confirmRemove() {
    if (!removing) return
    setBusy(true)
    try {
      const map: Record<string, (id: string) => Promise<unknown>> = {
        category: Service.deleteCategory,
        tier: Service.deleteTier,
        item: Service.deleteItem,
        group: Service.deleteGroup,
        text: Service.deleteTextBlock,
        option: Service.deleteOption,
        curve: Service.deleteCurve,
      }
      await map[removing.kind](removing.id)
      toast.success({ title: 'Removido', description: `"${removing.name}" saiu do cadastro.` })
      setRemoving(null); await load()
    } catch (e) {
      toast.error({ title: 'Não foi possível remover', description: extractApiError(e).message })
    } finally { setBusy(false) }
  }

  // ---------------------------------------------------------------- Exportação
  // Tudo já está em memória (endpoints sem paginação) — exporta direto.
  function exportRows<T>(filename: string, rows: T[], columns: ExportColumn<T>[]) {
    if (!rows.length) { toast.error('Nenhum registro para exportar.'); return }
    exportToXlsx(filename, rows, columns)
  }

  const itemExportCols: ExportColumn<ProposalCoverageItem>[] = [
    { header: 'Ordem',            accessor: i => i.sortOrder },
    { header: 'Cobertura',        accessor: i => i.name },
    { header: 'Descrição',        accessor: i => richToPlain(i.description) },
    { header: 'Usada nas linhas', accessor: i => (linesByItem[i.id] ?? []).join(', ') },
    { header: 'Status',           accessor: i => i.status },
  ]

  const groupExportCols: ExportColumn<ProposalBeneficiaryGroup>[] = [
    { header: 'Ordem',               accessor: g => g.sortOrder },
    { header: 'Grupo',               accessor: g => g.name },
    { header: 'Descrição',           accessor: g => richToPlain(g.description) },
    { header: 'Idade limite padrão', accessor: g => g.defaultAgeLimit != null ? `${g.defaultAgeLimit} anos` : 'Sem limite' },
    { header: 'Status',              accessor: g => g.status },
  ]

  const curveExportCols: ExportColumn<ProposalDiscountCurve>[] = [
    { header: 'Ordem',             accessor: c => c.sortOrder },
    { header: 'Curva de desconto', accessor: c => c.name },
    { header: 'Descrição',         accessor: c => richToPlain(c.description) },
    { header: 'Status',            accessor: c => c.status },
  ]

  const textExportCols: ExportColumn<ProposalTextBlock>[] = [
    { header: 'Tipo',      accessor: t => TEXT_KINDS.find(k => k.value === t.kind)?.label ?? t.kind },
    { header: 'Título',    accessor: t => t.title ?? '' },
    { header: 'Linha',     accessor: t => t.categoryName ?? 'Todas as linhas' },
    { header: 'Por padrão', accessor: t => t.autoInclude ? 'Entra marcado' : 'Marcar na proposta' },
    { header: 'Conteúdo',  accessor: t => richToPlain(t.content) },
    { header: 'Status',    accessor: t => t.status },
  ]

  // Categorias e tipologias: uma linha por tipologia (categoria sem tipologia
  // entra sozinha, para não sumir da planilha).
  type CategoryTierRow = { category: (typeof categories)[number]; tier: (typeof categories)[number]['tiers'][number] | null }
  const categoryExportRows: CategoryTierRow[] = categories.flatMap((category): CategoryTierRow[] =>
    category.tiers.length ? category.tiers.map(tier => ({ category, tier })) : [{ category, tier: null }])
  const categoryExportCols: ExportColumn<CategoryTierRow>[] = [
    { header: 'Categoria',          accessor: r => r.category.name },
    { header: 'Status da categoria', accessor: r => r.category.status },
    { header: 'Tipologia',          accessor: r => r.tier?.name ?? '' },
    { header: 'Quem pode aderir',   accessor: r => r.tier?.beneficiariesText ?? '' },
    { header: 'Curva',              accessor: r => r.tier?.discountCurveName ?? '' },
    { header: 'Status da tipologia', accessor: r => r.tier?.status ?? '' },
  ]

  // ---------------------------------------------------------------- Peças
  const exportBtn = (onClick: () => void) => (
    <Button variant="secondary" iconLeft={<Download size={15} />} onClick={onClick}>Exportar</Button>
  )
  const newBtn = (label: string, onClick: () => void) => canCreate
    ? <Button iconLeft={<Plus size={15} />} onClick={onClick}>{label}</Button>
    : null
  const rowActions = (onEdit: () => void, onRemove: () => void, name: string) => (
    <div className="flex justify-end">
      {canEdit && <RowIconButton label={`Editar ${name}`} onClick={onEdit}><Pencil size={15} /></RowIconButton>}
      {canDelete && <RowIconButton label={`Remover ${name}`} danger onClick={onRemove}><Trash2 size={15} /></RowIconButton>}
    </div>
  )
  const searchBox = (value: string, onChange: (v: string) => void, placeholder: string) => (
    <Input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      iconLeft={<Search size={15} />} clearable wrapperClassName="w-80 max-w-full" aria-label={placeholder} />
  )
  /** Texto aberto da linha, com "Editar" ao lado. Fechado ou vazio: nada. */
  const textDetail = (key: string, html: string | null | undefined, onEdit: () => void, note?: string) =>
    exp.isOpen(key) && wordCount(html) > 0
      ? <TextPanel html={html} aside={<>
          {note && <p className="text-xs text-gray-500">{note}</p>}
          {canEdit && <Button size="sm" variant="secondary" iconLeft={<Pencil size={14} />} onClick={onEdit}>Editar texto</Button>}
        </>} />
      : null
  const ACTIONS_COL = 104
  const STATUS_COL = 128

  // ---------------------------------------------------------------- Categorias
  const q = normalizeForMatch(catQuery)
  const catGroups: FixedGroup<ProposalTier>[] = categories.map(category => {
    const tiers = q
      ? category.tiers.filter(t => normalizeForMatch(`${t.name} ${t.beneficiariesText ?? ''} ${category.name}`).includes(q))
      : category.tiers
    return {
      key: category.id,
      rows: tiers,
      empty: category.tiers.length === 0
        ? <>Sem tipologias, a linha não aparece nas propostas.{canCreate && <> <button type="button" className="cursor-pointer font-semibold text-brand-600 hover:underline"
            onClick={() => setTierForm({ categoryId: category.id, sortOrder: 1, status: 'ATIVO', groups: [] })}>Adicionar a primeira</button></>}</>
        : 'Nenhuma tipologia desta categoria corresponde à busca.',
      header: (
        <div className="flex flex-wrap items-center gap-3">
          <span className={`text-[15px] font-semibold ${category.status === 'ATIVO' ? 'text-gray-900' : 'text-gray-500'}`}>{category.name}</span>
          <span className="text-[13px] text-gray-500">
            {category.tiers.length === 0 ? 'nenhuma tipologia' : `${category.tiers.length} tipologia${category.tiers.length > 1 ? 's' : ''}`}
          </span>
          <StatusPill status={category.status} feminine />
          {category.description && <RichHtml html={category.description} className="basis-full text-xs text-gray-500" />}
          <span className="ml-auto flex items-center gap-1">
            {canCreate && (
              <Button size="sm" variant="ghost" iconLeft={<Plus size={14} />}
                onClick={() => setTierForm({ categoryId: category.id, sortOrder: category.tiers.length + 1, status: 'ATIVO', groups: [] })}>
                Tipologia
              </Button>
            )}
            {canEdit && <RowIconButton label={`Editar categoria ${category.name}`} onClick={() => setCategoryForm(category)}><Pencil size={15} /></RowIconButton>}
            {canDelete && <RowIconButton label={`Remover categoria ${category.name}`} danger onClick={() => setRemoving({ kind: 'category', id: category.id, name: category.name })}><Trash2 size={15} /></RowIconButton>}
          </span>
        </div>
      ),
    }
  })
  const tierCount = categories.reduce((n, c) => n + c.tiers.length, 0)
  const tierCols: FixedColumn<ProposalTier>[] = [
    { key: 'name', header: 'Tipologia', width: 220, render: t => <span className="font-medium text-gray-900">{t.name}</span> },
    { key: 'who', header: 'Quem pode aderir', render: t => t.beneficiariesText || <span className="text-gray-400">Nenhum grupo definido</span> },
    { key: 'curve', header: 'Curva', width: 160, render: t => t.discountCurveName || <span className="text-gray-400">Sem curva</span> },
    { key: 'status', header: 'Status', width: STATUS_COL, render: t => <StatusPill status={t.status} feminine /> },
    { key: 'actions', header: <span className="sr-only">Ações</span>, width: ACTIONS_COL, align: 'right', render: t => {
      const category = categories.find(c => c.tiers.some(x => x.id === t.id))
      return rowActions(() => setTierForm({ ...t, categoryId: category?.id ?? '' }), () => setRemoving({ kind: 'tier', id: t.id, name: t.name }), t.name)
    } },
  ]

  // ---------------------------------------------------------------- Coberturas
  const iq = normalizeForMatch(itemQuery)
  const visibleItems = items
    .filter(i => itemStatus === 'all' || (itemStatus === 'on' ? i.status === 'ATIVO' : i.status !== 'ATIVO'))
    .filter(i => !iq || normalizeForMatch(`${i.name} ${richToPlain(i.description)}`).includes(iq))
  const itemCols: FixedColumn<ProposalCoverageItem>[] = [
    { key: 'order', header: 'Ordem', width: 72, render: i => <span className="tabular-nums text-gray-500">{i.sortOrder}</span> },
    { key: 'name', header: 'Cobertura', render: i => (
      <div className="space-y-0.5 py-1">
        <p className={`font-medium ${i.status === 'ATIVO' ? 'text-gray-900' : 'text-gray-500'}`}>{i.name}</p>
        <TextToggle open={exp.isOpen(`item:${i.id}`)} onToggle={() => exp.toggle(`item:${i.id}`)} html={i.description} />
      </div>
    ) },
    { key: 'lines', header: 'Usada nas linhas', width: 220, render: i => (linesByItem[i.id]?.length
      ? <span className="flex flex-wrap gap-1">{linesByItem[i.id].map(n => <span key={n} className="rounded-md bg-gray-100 px-2 py-0.5 text-xs text-gray-700">{n}</span>)}</span>
      : <span className="text-xs text-gray-400">Nenhuma linha</span>) },
    { key: 'status', header: 'Status', width: STATUS_COL, render: i => <StatusPill status={i.status} feminine /> },
    { key: 'actions', header: <span className="sr-only">Ações</span>, width: ACTIONS_COL, align: 'right',
      render: i => rowActions(() => setItemForm(i), () => setRemoving({ kind: 'item', id: i.id, name: i.name }), i.name) },
  ]

  // ---------------------------------------------------------------- Grupos
  const groupCols: FixedColumn<ProposalBeneficiaryGroup>[] = [
    { key: 'order', header: 'Ordem', width: 72, render: g => <span className="tabular-nums text-gray-500">{g.sortOrder}</span> },
    { key: 'name', header: 'Grupo', render: g => (
      <div className="space-y-0.5 py-1">
        <p className="font-medium text-gray-900">{g.name}</p>
        {wordCount(g.description) > 0 && <TextToggle open={exp.isOpen(`group:${g.id}`)} onToggle={() => exp.toggle(`group:${g.id}`)}
          html={g.description} kind="Descrição" labels={['Ver descrição', 'Ocultar descrição']} />}
      </div>
    ) },
    { key: 'age', header: 'Idade limite padrão', width: 180, render: g => g.defaultAgeLimit != null ? `${g.defaultAgeLimit} anos` : <span className="text-gray-400">Sem limite</span> },
    { key: 'status', header: 'Status', width: STATUS_COL, render: g => <StatusPill status={g.status} /> },
    { key: 'actions', header: <span className="sr-only">Ações</span>, width: ACTIONS_COL, align: 'right',
      render: g => rowActions(() => setGroupForm(g), () => setRemoving({ kind: 'group', id: g.id, name: g.name }), g.name) },
  ]

  // ---------------------------------------------------------------- Textos
  const textGroups: FixedGroup<ProposalTextBlock>[] = TEXT_KINDS
    .map(kind => ({ kind, rows: texts.filter(t => t.kind === kind.value) }))
    .filter(g => g.rows.length > 0)
    .map(({ kind, rows }) => ({
      key: kind.value,
      rows,
      header: (
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
          <span className="text-sm font-semibold text-gray-900">{kind.label}</span>
          <span className="text-xs text-gray-500">{kind.where} · {rows.length} texto{rows.length > 1 ? 's' : ''}</span>
        </div>
      ),
    }))
  const textCols: FixedColumn<ProposalTextBlock>[] = [
    { key: 'title', header: 'Texto', render: t => (
      <div className="space-y-0.5 py-1">
        <p className="font-medium text-gray-900">{t.title || 'Sem título'}</p>
        <TextToggle open={exp.isOpen(`text:${t.id}`)} onToggle={() => exp.toggle(`text:${t.id}`)} html={t.content}
          emptyLabel="Sem conteúdo — o bloco não imprime nada." />
      </div>
    ) },
    { key: 'line', header: 'Linha', width: 160, render: t => <span className="rounded-md bg-gray-100 px-2 py-0.5 text-xs text-gray-700">{t.categoryName ?? 'Todas as linhas'}</span> },
    { key: 'auto', header: 'Nas propostas novas', width: 170, render: t => t.autoInclude ? 'Entra marcado' : <span className="text-gray-500">Marcar na proposta</span> },
    { key: 'status', header: 'Status', width: STATUS_COL, render: t => <StatusPill status={t.status} /> },
    { key: 'actions', header: <span className="sr-only">Ações</span>, width: ACTIONS_COL, align: 'right',
      render: t => rowActions(() => setTextForm(t), () => setRemoving({ kind: 'text', id: t.id, name: t.title ?? 'bloco' }), t.title ?? 'bloco') },
  ]

  // ---------------------------------------------------------------- Opções
  const kindOptions = options.filter(o => o.kind === optionKind)
  const optionCols: FixedColumn<ProposalOption>[] = [
    { key: 'label', header: 'Texto na proposta', render: o => (
      <div className="space-y-0.5 py-1">
        <p className="font-medium text-gray-900">{o.label}</p>
        <TextToggle open={exp.isOpen(`option:${o.id}`)} onToggle={() => exp.toggle(`option:${o.id}`)} html={o.description}
          kind={o.kind === 'CONTRACT_TYPE' ? 'Cláusula' : 'Descrição'} labels={['Ver descrição', 'Ocultar descrição']}
          emptyLabel="Sem descrição — no PDF sai só o nome" />
      </div>
    ) },
    { key: 'value', header: 'Valor interno', width: 140, render: o => o.value ? <span className="tabular-nums text-gray-600">{o.value}</span> : <span className="text-gray-400">—</span> },
    { key: 'default', header: 'Padrão', width: 120, render: o => o.isDefault ? <Pill tone="blue" dot={false}>Padrão</Pill> : <span className="text-gray-400">—</span> },
    { key: 'status', header: 'Status', width: STATUS_COL, render: o => <StatusPill status={o.status} feminine /> },
    { key: 'actions', header: <span className="sr-only">Ações</span>, width: ACTIONS_COL, align: 'right',
      render: o => rowActions(() => setOptionForm(o), () => setRemoving({ kind: 'option', id: o.id, name: o.label }), o.label) },
  ]

  // ---------------------------------------------------------------- Curvas
  const curveCols: FixedColumn<ProposalDiscountCurve>[] = [
    { key: 'order', header: 'Ordem', width: 72, render: c => <span className="tabular-nums text-gray-500">{c.sortOrder}</span> },
    { key: 'name', header: 'Curva de desconto', render: c => (
      <div className="space-y-0.5 py-1">
        <p className="font-medium text-gray-900">{c.name}</p>
        {wordCount(c.description) > 0 && <TextToggle open={exp.isOpen(`curve:${c.id}`)} onToggle={() => exp.toggle(`curve:${c.id}`)}
          html={c.description} kind="Descrição" labels={['Ver descrição', 'Ocultar descrição']} />}
      </div>
    ) },
    { key: 'status', header: 'Status', width: STATUS_COL, render: c => <StatusPill status={c.status} feminine /> },
    { key: 'actions', header: <span className="sr-only">Ações</span>, width: ACTIONS_COL, align: 'right',
      render: c => rowActions(() => setCurveForm(c), () => setRemoving({ kind: 'curve', id: c.id, name: c.name }), c.name) },
  ]

  const card = (children: React.ReactNode) => (
    <section className="overflow-hidden rounded-xl bg-white ring-1 ring-gray-200">{children}</section>
  )
  const toolbar = (children: React.ReactNode) => (
    <div className="flex flex-wrap items-center justify-between gap-3 px-6 pb-4">{children}</div>
  )

  const sections: FormSection[] = [
    {
      key: 'categories', label: 'Categorias e tipologias', icon: Layers, count: categories.length,
      content: card(<>
        <SectionHeader title="Categorias e tipologias"
          description="Cada categoria (linha) e as tipologias que aparecem no orçamento, com quem pode aderir e a curva de desconto."
          actions={<>
            {categories.length > 0 && exportBtn(() => exportRows('categorias-tipologias', categoryExportRows, categoryExportCols))}
            {newBtn('Nova categoria', () => setCategoryForm({ sortOrder: categories.length + 1, status: 'ATIVO' }))}
          </>} />
        {toolbar(<>
          {searchBox(catQuery, setCatQuery, 'Buscar tipologia ou quem pode aderir')}
          <span className="text-[13px] text-gray-500">{categories.length} categoria{categories.length === 1 ? '' : 's'} · {tierCount} tipologia{tierCount === 1 ? '' : 's'}</span>
        </>)}
        <div className="border-t border-gray-200">
          <FixedTable columns={tierCols} groups={catGroups} getRowKey={t => t.id} loading={loading}
            emptyMessage="Nenhuma categoria cadastrada." minWidth={860} />
        </div>
      </>),
    },
    {
      key: 'items', label: 'Coberturas', icon: ShieldPlus, count: items.length,
      content: card(<>
        <SectionHeader title="Coberturas"
          description="O texto geral de cada cobertura. Em que linha ela entra, com que texto, carência e peso, fica em Linhas e coberturas."
          actions={<>
            {items.length > 0 && exportBtn(() => exportRows('coberturas', items, itemExportCols))}
            {newBtn('Nova cobertura', () => setItemForm({ sortOrder: items.length + 1, status: 'ATIVO' }))}
          </>} />
        {toolbar(<>
          {searchBox(itemQuery, setItemQuery, 'Buscar cobertura')}
          <div role="group" aria-label="Filtrar por status" className="inline-flex rounded-lg bg-gray-100 p-0.5">
            {([['all', `Todas · ${items.length}`], ['on', `Ativas · ${items.filter(i => i.status === 'ATIVO').length}`], ['off', `Inativas · ${items.filter(i => i.status !== 'ATIVO').length}`]] as const).map(([k, label]) => (
              <button key={k} type="button" aria-pressed={itemStatus === k} onClick={() => setItemStatus(k)}
                className={`h-8 cursor-pointer rounded-md px-3 text-[13px] ${itemStatus === k ? 'bg-white font-semibold text-gray-900 shadow-sm' : 'font-medium text-gray-600 hover:text-gray-900'}`}>
                {label}
              </button>
            ))}
          </div>
        </>)}
        <div className="border-t border-gray-200">
          <FixedTable columns={itemCols} rows={visibleItems} getRowKey={i => i.id} loading={loading}
            detail={i => textDetail(`item:${i.id}`, i.description, () => setItemForm(i), 'Texto geral da cobertura. Cada linha pode ter o seu em Linhas e coberturas.')}
            rowClassName={i => (i.status !== 'ATIVO' ? 'bg-gray-50/60' : undefined)}
            emptyMessage={items.length ? 'Nenhuma cobertura corresponde ao filtro.' : 'Nenhuma cobertura cadastrada.'} minWidth={880} />
        </div>
      </>),
    },
    {
      key: 'groups', label: 'Beneficiários', icon: Users, count: groups.length,
      content: card(<>
        <SectionHeader title="Grupos de beneficiários"
          description="Quem pode aderir. A frase de cada tipologia é montada combinando estes grupos, com o limite de idade de cada vínculo."
          actions={<>
            {groups.length > 0 && exportBtn(() => exportRows('grupos-beneficiarios', groups, groupExportCols))}
            {newBtn('Novo grupo', () => setGroupForm({ sortOrder: groups.length + 1, status: 'ATIVO' }))}
          </>} />
        <div className="border-t border-gray-200">
          <FixedTable columns={groupCols} rows={groups} getRowKey={g => g.id} loading={loading}
            detail={g => textDetail(`group:${g.id}`, g.description, () => setGroupForm(g))}
            emptyMessage="Nenhum grupo cadastrado." minWidth={760} />
        </div>
      </>),
    },
    {
      key: 'texts', label: 'Textos do PDF', icon: FileText, count: texts.length,
      content: card(<>
        <SectionHeader title="Textos do PDF"
          description="Blocos fixos que o PDF imprime. Escreva uma vez aqui; na proposta, só se marca o que entra. Alterar vale para as próximas propostas — as já geradas mantêm o texto delas."
          actions={<>
            {texts.length > 0 && exportBtn(() => exportRows('textos-pdf', texts, textExportCols))}
            {newBtn('Novo texto', () => setTextForm({ kind: 'OUTROS', sortOrder: texts.length + 1, status: 'ATIVO', autoInclude: false }))}
          </>} />
        <div className="border-t border-gray-200">
          <FixedTable columns={textCols} groups={textGroups} getRowKey={t => t.id} loading={loading}
            detail={t => textDetail(`text:${t.id}`, t.content, () => setTextForm(t))}
            emptyMessage="Nenhum texto cadastrado." minWidth={900} />
        </div>
      </>),
    },
    {
      key: 'options', label: 'Listas de seleção', icon: SlidersHorizontal, count: options.length,
      content: card(<>
        <SectionHeader title="Listas de seleção"
          description="Campos da proposta que se escolhem numa lista. Escolha o campo e cadastre as opções dele." />
        <div className="flex flex-wrap border-t border-gray-200">
          <div role="tablist" aria-label="Campo da proposta" aria-orientation="vertical"
            className="flex flex-[1_1_220px] flex-col gap-0.5 border-gray-200 p-3 md:max-w-[260px] md:border-r">
            {OPTION_KINDS.map(kind => {
              const on = kind === optionKind
              const n = options.filter(o => o.kind === kind).length
              return (
                <button key={kind} type="button" role="tab" aria-selected={on} onClick={() => setOptionKind(kind)}
                  className={`flex min-h-[38px] cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-left text-sm ${on ? 'bg-brand-50 font-semibold text-brand-700' : 'font-medium text-gray-700 hover:bg-gray-50'}`}>
                  <span className="flex-1">{OPTION_KIND_LABELS[kind]}</span>
                  <span className={`text-xs tabular-nums ${on ? 'text-brand-700' : n === 0 ? 'text-amber-600' : 'text-gray-400'}`}>{n}</span>
                </button>
              )
            })}
          </div>
          <div role="tabpanel" className="min-w-0 flex-[999_1_480px]">
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
              <div className="min-w-0">
                <h3 className="text-[15px] font-semibold text-gray-900">{OPTION_KIND_LABELS[optionKind]}</h3>
                <p className="text-[13px] text-gray-500">{OPTION_KIND_HELP[optionKind]}</p>
              </div>
              {newBtn('Nova opção', () => setOptionForm({ kind: optionKind, sortOrder: kindOptions.length + 1, status: 'ATIVO', isDefault: false }))}
            </div>
            <div className="border-t border-gray-200">
              <FixedTable columns={optionCols} rows={kindOptions} getRowKey={o => o.id} loading={loading}
                detail={o => textDetail(`option:${o.id}`, o.description, () => setOptionForm(o))}
                emptyMessage="Nenhuma opção cadastrada — o campo aparece vazio na proposta." minWidth={640} />
            </div>
          </div>
        </div>
      </>),
    },
    {
      key: 'curves', label: 'Curvas de desconto', icon: Percent, count: curves.length,
      content: card(<>
        <SectionHeader title="Curvas de desconto"
          description="Cada tipologia usa uma curva para saber quanto o preço cai conforme o número de vidas. Aqui ficam só os nomes; as faixas de vidas ficam em Tabelas de preço, porque mudam a cada versão."
          actions={<>
            {curves.length > 0 && exportBtn(() => exportRows('curvas-desconto', curves, curveExportCols))}
            {newBtn('Nova curva', () => setCurveForm({ sortOrder: curves.length + 1, status: 'ATIVO' }))}
          </>} />
        <div className="border-t border-gray-200">
          <FixedTable columns={curveCols} rows={curves} getRowKey={c => c.id} loading={loading}
            detail={c => textDetail(`curve:${c.id}`, c.description, () => setCurveForm(c))}
            emptyMessage="Nenhuma curva cadastrada." minWidth={640} />
        </div>
      </>),
    },
  ]

  return (
    <>
      <PageHeader
        title="Catálogo de propostas"
        subtitle="Cadastros: linhas, modalidades, coberturas, textos e opções que a proposta usa"
        crumbs={[{ label: 'Configurações', to: '/settings' }, { label: 'Comercial' }, { label: 'Cadastros' }]}
      />
      <ProposalCatalogNav />

      <FormSidemenu sections={sections} activeKey={tab} onChange={(k) => setTab(k as Tab)} />

      {/* ------------------------------------------------------- Formulários */}
      <CategoryModal form={categoryForm} busy={busy} onClose={() => setCategoryForm(null)}
        onSave={(payload) => save(() => Service.saveCategory(payload), () => setCategoryForm(null))} />

      <TierModal form={tierForm} busy={busy} curves={curves} groups={groups}
        onClose={() => setTierForm(null)}
        onSave={(payload) => save(() => Service.saveTier(payload), () => setTierForm(null))} />

      <ItemModal form={itemForm} busy={busy} onClose={() => setItemForm(null)}
        onSave={(payload) => save(() => Service.saveItem(payload), () => setItemForm(null))} />

      <GroupModal form={groupForm} busy={busy} onClose={() => setGroupForm(null)}
        onSave={(payload) => save(() => Service.saveGroup(payload), () => setGroupForm(null))} />

      <TextModal form={textForm} busy={busy} categories={categories} onClose={() => setTextForm(null)}
        onSave={(payload) => save(() => Service.saveTextBlock(payload), () => setTextForm(null))} />

      <OptionModal form={optionForm} busy={busy} onClose={() => setOptionForm(null)}
        onSave={(payload) => save(() => Service.saveOption(payload), () => setOptionForm(null))} />

      <CurveModal form={curveForm} busy={busy} onClose={() => setCurveForm(null)}
        onSave={(payload) => save(() => Service.saveCurve(payload), () => setCurveForm(null))} />

      <ConfirmDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={confirmRemove}
        title="Excluir cadastro?"
        description={removing?.kind === 'text' || removing?.kind === 'group'
          ? `Excluir "${removing?.name}"?`
          : `Excluir "${removing?.name}"? Ele some do catálogo e das propostas novas. As propostas já feitas continuam com ele — nome, valores e PDF.`}
        confirmLabel="Remover"
        variant="danger"
      />
    </>
  )
}

// =========================================================== Modais

function CategoryModal({ form, busy, onClose, onSave }: {
  form: Partial<ProposalCategory> | null
  busy: boolean
  onClose: () => void
  onSave: (p: { id?: string; name: string; description?: string | null; sortOrder?: number; status?: string }) => void
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [sortOrder, setSortOrder] = useState<number | null>(0)
  const [status, setStatus] = useState<'ATIVO' | 'INATIVO'>('ATIVO')

  useEffect(() => {
    if (!form) return
    setName(form.name ?? ''); setDescription(form.description ?? '')
    setSortOrder(form.sortOrder ?? 0); setStatus((form.status as 'ATIVO') ?? 'ATIVO')
  }, [form])

  return (
    <Modal open={!!form} onClose={onClose} title={form?.id ? 'Editar categoria' : 'Nova categoria'} size="md"
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button loading={busy} disabled={!name.trim()}
          onClick={() => onSave({ id: form?.id, name: name.trim(), description: description.trim() || null, sortOrder: sortOrder ?? 0, status })}>
          Salvar
        </Button>
      </>}>
      <div className="space-y-4">
        <Input label="Nome" required value={name} onChange={e => setName(e.target.value)} placeholder="Ex.: Bronze" />
        <RichTextEditor compact label="Descrição" value={description} onChange={setDescription} />
        <NumberInput label="Ordem" value={sortOrder} onChange={setSortOrder} min={0} />
        <StatusToggle value={status} onChange={setStatus} />
      </div>
    </Modal>
  )
}

function TierModal({ form, busy, curves, groups, onClose, onSave }: {
  form: (Partial<ProposalTier> & { categoryId: string }) | null
  busy: boolean
  curves: ProposalDiscountCurve[]
  groups: ProposalBeneficiaryGroup[]
  onClose: () => void
  onSave: (p: {
    id?: string
    proposalCategoryId: string
    name: string
    discountCurveId?: string | null
    beneficiariesLabel?: string | null
    minAge?: number | null
    maxAge?: number | null
    sortOrder?: number
    status?: string
    groups?: { id: string; age_limit?: number | null; min_age?: number | null }[]
  }) => void
}) {
  const [name, setName] = useState('')
  const [curveId, setCurveId] = useState('')
  const [label, setLabel] = useState('')
  const [minAge, setMinAge] = useState<number | null>(null)
  const [maxAge, setMaxAge] = useState<number | null>(null)
  const [sortOrder, setSortOrder] = useState<number | null>(0)
  const [status, setStatus] = useState<'ATIVO' | 'INATIVO'>('ATIVO')
  const [picked, setPicked] = useState<Record<string, { on: boolean; ageLimit: number | null; minAge: number | null }>>({})

  useEffect(() => {
    if (!form) return
    setName(form.name ?? ''); setCurveId(form.discountCurveId ?? '')
    setLabel(form.beneficiariesLabel ?? '')
    setMinAge(form.minAge ?? null); setMaxAge(form.maxAge ?? null)
    setSortOrder(form.sortOrder ?? 0); setStatus((form.status as 'ATIVO') ?? 'ATIVO')

    const next: Record<string, { on: boolean; ageLimit: number | null; minAge: number | null }> = {}
    for (const g of groups) next[g.id] = { on: false, ageLimit: g.defaultAgeLimit ?? null, minAge: null }
    for (const g of form.groups ?? []) next[g.id] = { on: true, ageLimit: g.ageLimit, minAge: g.minAge }
    setPicked(next)
  }, [form, groups])

  function toggle(id: string) {
    setPicked(p => ({ ...p, [id]: { ...p[id], on: !p[id]?.on } }))
  }

  return (
    <Modal open={!!form} onClose={onClose} title={form?.id ? 'Editar tipologia' : 'Nova tipologia'} size="lg"
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button loading={busy} disabled={!name.trim()} onClick={() => onSave({
          id: form?.id,
          proposalCategoryId: form!.categoryId,
          name: name.trim(),
          discountCurveId: curveId || null,
          beneficiariesLabel: label.trim() || null,
          minAge, maxAge,
          sortOrder: sortOrder ?? 0,
          status,
          groups: groups.filter(g => picked[g.id]?.on).map(g => ({
            id: g.id,
            age_limit: picked[g.id]?.ageLimit ?? null,
            min_age: picked[g.id]?.minAge ?? null,
          })),
        })}>Salvar</Button>
      </>}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input label="Nome" required value={name} onChange={e => setName(e.target.value)} placeholder="Ex.: Familiar" />
          <Select label="Curva de desconto" value={curveId} onChange={e => setCurveId(e.target.value)}
            options={curves.map(c => ({ value: c.id, label: c.name }))}
            placeholder="Sem desconto por volume"
            hint="Define quanto o preço cai conforme o número de vidas." />
        </div>

        <Input label="Quem pode aderir (texto livre)" value={label} onChange={e => setLabel(e.target.value)}
          placeholder="Deixe em branco para montar pelos grupos abaixo"
          hint="Quando preenchido, este texto substitui a frase montada pelos grupos." />

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <NumberInput label="Idade mínima" value={minAge} onChange={setMinAge} min={0} max={120} placeholder="Sem mínimo" />
          <NumberInput label="Idade máxima" value={maxAge} onChange={setMaxAge} min={0} max={120} placeholder="Sem máximo" />
          <NumberInput label="Ordem" value={sortOrder} onChange={setSortOrder} min={0} />
        </div>

        <div className="border-t border-gray-100 pt-4">
          <div className="mb-1 flex items-center gap-1.5 text-sm font-medium text-gray-700">
            <Users size={14} className="text-gray-400" /> Grupos que fazem parte
          </div>
          <p className="mb-3 text-xs text-gray-500">
            Marque quem entra nesta tipologia. O limite de idade aqui vale só para ela — é onde
            &quot;filhos até 18&quot; vira &quot;filhos até 29&quot;.
          </p>

          {groups.length === 0 ? (
            <p className="text-xs text-gray-500">Nenhum grupo cadastrado ainda.</p>
          ) : (
            <div className="space-y-2">
              {groups.map(g => (
                <div key={g.id} className="flex flex-wrap items-center gap-3 rounded-md border border-gray-200 bg-gray-50/60 px-3 py-2">
                  <Toggle checked={!!picked[g.id]?.on} onChange={() => toggle(g.id)} label={g.name} />
                  {picked[g.id]?.on && (
                    <div className="ml-auto flex items-center gap-2">
                      <NumberInput label="Até (anos)" value={picked[g.id]?.ageLimit ?? null}
                        onChange={v => setPicked(p => ({ ...p, [g.id]: { ...p[g.id], ageLimit: v } }))}
                        min={0} max={120} placeholder="—" wrapperClassName="w-32" />
                      <NumberInput label="A partir de" value={picked[g.id]?.minAge ?? null}
                        onChange={v => setPicked(p => ({ ...p, [g.id]: { ...p[g.id], minAge: v } }))}
                        min={0} max={120} placeholder="—" wrapperClassName="w-32" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <StatusToggle value={status} onChange={setStatus} />
      </div>
    </Modal>
  )
}

function ItemModal({ form, busy, onClose, onSave }: {
  form: Partial<ProposalCoverageItem> | null
  busy: boolean
  onClose: () => void
  onSave: (p: { id?: string; name: string; description?: string | null; sortOrder?: number; status?: string }) => void
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [sortOrder, setSortOrder] = useState<number | null>(0)
  const [status, setStatus] = useState<'ATIVO' | 'INATIVO'>('ATIVO')

  useEffect(() => {
    if (!form) return
    setName(form.name ?? ''); setDescription(form.description ?? '')
    setSortOrder(form.sortOrder ?? 0); setStatus((form.status as 'ATIVO') ?? 'ATIVO')
  }, [form])

  return (
    <Modal open={!!form} onClose={onClose} title={form?.id ? 'Editar item de cobertura' : 'Novo item de cobertura'} size="lg"
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button loading={busy} disabled={!name.trim()} onClick={() => onSave({
          id: form?.id, name: name.trim(), description: description.trim() || null,
          sortOrder: sortOrder ?? 0, status,
        })}>Salvar</Button>
      </>}>
      <div className="space-y-4">
        <Input label="Nome" required value={name} onChange={e => setName(e.target.value)}
          placeholder="Ex.: Orientação Médica & Telemedicina" />
        <RichTextEditor label="Descrição" value={description} onChange={setDescription}
          hint="Texto geral da cobertura no PDF. Cada linha pode ter o seu (com capital e carência) em Linhas e coberturas." />
        <NumberInput label="Ordem" value={sortOrder} onChange={setSortOrder} min={0} />

        <StatusToggle value={status} onChange={setStatus} />
      </div>
    </Modal>
  )
}

function GroupModal({ form, busy, onClose, onSave }: {
  form: Partial<ProposalBeneficiaryGroup> | null
  busy: boolean
  onClose: () => void
  onSave: (p: { id?: string; name: string; description?: string | null; defaultAgeLimit?: number | null; sortOrder?: number; status?: string }) => void
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [ageLimit, setAgeLimit] = useState<number | null>(null)
  const [sortOrder, setSortOrder] = useState<number | null>(0)
  const [status, setStatus] = useState<'ATIVO' | 'INATIVO'>('ATIVO')

  useEffect(() => {
    if (!form) return
    setName(form.name ?? ''); setDescription(form.description ?? '')
    setAgeLimit(form.defaultAgeLimit ?? null)
    setSortOrder(form.sortOrder ?? 0); setStatus((form.status as 'ATIVO') ?? 'ATIVO')
  }, [form])

  return (
    <Modal open={!!form} onClose={onClose} title={form?.id ? 'Editar grupo' : 'Novo grupo de beneficiários'} size="md"
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button loading={busy} disabled={!name.trim()} onClick={() => onSave({
          id: form?.id, name: name.trim(), description: description.trim() || null,
          defaultAgeLimit: ageLimit, sortOrder: sortOrder ?? 0, status,
        })}>Salvar</Button>
      </>}>
      <div className="space-y-4">
        <Input label="Nome" required value={name} onChange={e => setName(e.target.value)} placeholder="Ex.: Filhos(as)" />
        <RichTextEditor compact label="Descrição" value={description} onChange={setDescription} />
        <NumberInput label="Idade limite padrão" value={ageLimit} onChange={setAgeLimit} min={0} max={120}
          placeholder="Sem limite" hint="Pode ser sobrescrito em cada tipologia." />
        <NumberInput label="Ordem" value={sortOrder} onChange={setSortOrder} min={0} />
        <StatusToggle value={status} onChange={setStatus} />
      </div>
    </Modal>
  )
}

function TextModal({ form, busy, categories, onClose, onSave }: {
  form: Partial<ProposalTextBlock> | null
  busy: boolean
  categories: ProposalCategory[]
  onClose: () => void
  onSave: (p: { id?: string; kind: TextBlockKind; title?: string | null; content?: string | null; proposalCategoryId?: string | null; autoInclude?: boolean; sortOrder?: number; status?: string }) => void
}) {
  const [kind, setKind] = useState<TextBlockKind>('OUTROS')
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [autoInclude, setAutoInclude] = useState(false)
  const [sortOrder, setSortOrder] = useState<number | null>(0)
  const [status, setStatus] = useState<'ATIVO' | 'INATIVO'>('ATIVO')

  useEffect(() => {
    if (!form) return
    setKind((form.kind as TextBlockKind) ?? 'OUTROS')
    setTitle(form.title ?? ''); setContent(form.content ?? '')
    setCategoryId(form.categoryId ?? '')
    setAutoInclude(!!form.autoInclude)
    setSortOrder(form.sortOrder ?? 0); setStatus((form.status as 'ATIVO') ?? 'ATIVO')
  }, [form])

  return (
    <Modal open={!!form} onClose={onClose} title={form?.id ? 'Editar texto' : 'Novo texto da proposta'} size="lg"
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button loading={busy} onClick={() => onSave({
          id: form?.id, kind, title: title.trim() || null, content: content || null,
          proposalCategoryId: categoryId || null, autoInclude, sortOrder: sortOrder ?? 0, status,
        })}>Salvar</Button>
      </>}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Select label="Tipo" value={kind} onChange={e => setKind(e.target.value as TextBlockKind)}
            options={TEXT_KINDS} clearable={false} />
          <Select label="Categoria" value={categoryId} onChange={e => setCategoryId(e.target.value)}
            options={categories.map(c => ({ value: c.id, label: c.name }))}
            placeholder="Todas as categorias" />
        </div>
        <Input label="Título" value={title} onChange={e => setTitle(e.target.value)}
          placeholder="Ex.: Condições contratuais" />
        <RichTextEditor label="Conteúdo" value={content} onChange={setContent}
          hint="Sai no PDF do jeito que está aqui: use listas para as cláusulas, negrito para destacar." />
        <div className="rounded-md ring-1 ring-gray-200 bg-gray-50/50 px-3 py-2.5">
          <Toggle checked={autoInclude} onChange={setAutoInclude} label="Entra por padrão nas propostas novas" />
        </div>
        <NumberInput label="Ordem" value={sortOrder} onChange={setSortOrder} min={0} />
        <StatusToggle value={status} onChange={setStatus} />
      </div>
    </Modal>
  )
}

function OptionModal({ form, busy, onClose, onSave }: {
  form: Partial<ProposalOption> | null
  busy: boolean
  onClose: () => void
  onSave: (p: { id?: string; kind: OptionKind; label: string; value?: string | null; description?: string | null; isDefault?: boolean; sortOrder?: number; status?: string }) => void
}) {
  const [kind, setKind] = useState<OptionKind>('PAYMENT_DAY')
  const [label, setLabel] = useState('')
  const [value, setValue] = useState('')
  const [description, setDescription] = useState('')
  const [isDefault, setIsDefault] = useState(false)
  const [sortOrder, setSortOrder] = useState<number | null>(0)
  const [status, setStatus] = useState<'ATIVO' | 'INATIVO'>('ATIVO')

  useEffect(() => {
    if (!form) return
    setKind((form.kind as OptionKind) ?? 'PAYMENT_DAY')
    setLabel(form.label ?? ''); setValue(form.value ?? ''); setDescription(form.description ?? '')
    setIsDefault(!!form.isDefault)
    setSortOrder(form.sortOrder ?? 0); setStatus((form.status as 'ATIVO') ?? 'ATIVO')
  }, [form])

  return (
    <Modal open={!!form} onClose={onClose} title={form?.id ? 'Editar opção' : 'Nova opção'} size="md"
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button loading={busy} disabled={!label.trim()} onClick={() => onSave({
          id: form?.id, kind, label: label.trim(), value: value.trim() || null,
          description: description.trim() || null, isDefault, sortOrder: sortOrder ?? 0, status,
        })}>Salvar</Button>
      </>}>
      <div className="space-y-4">
        <Select label="Campo da proposta" value={kind} onChange={e => setKind(e.target.value as OptionKind)}
          options={OPTION_KINDS.map(k => ({ value: k, label: OPTION_KIND_LABELS[k] }))} clearable={false} />
        <Input label="Texto que aparece na proposta" required value={label} onChange={e => setLabel(e.target.value)}
          placeholder="Ex.: Todo 5º dia útil do mês" />
        <Input label="Valor interno" value={value} onChange={e => setValue(e.target.value)}
          placeholder="Opcional" hint="Usado quando a opção representa um número (ex.: 12 para 12 meses)." />
        <RichTextEditor compact label="Descrição" value={description} onChange={setDescription}
          hint="Modalidades de contratação usam a descrição como cláusula no PDF." />
        <div className="rounded-md ring-1 ring-gray-200 bg-gray-50/50 px-3 py-2.5">
          <Toggle checked={isDefault} onChange={setIsDefault} label="Opção padrão deste campo" />
          <p className="mt-1 text-xs text-gray-500">Só uma opção por campo pode ser a padrão.</p>
        </div>
        <NumberInput label="Ordem" value={sortOrder} onChange={setSortOrder} min={0} />
        <StatusToggle value={status} onChange={setStatus} />
      </div>
    </Modal>
  )
}

function CurveModal({ form, busy, onClose, onSave }: {
  form: Partial<ProposalDiscountCurve> | null
  busy: boolean
  onClose: () => void
  onSave: (p: { id?: string; name: string; description?: string | null; sortOrder?: number; status?: string }) => void
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [sortOrder, setSortOrder] = useState<number | null>(0)
  const [status, setStatus] = useState<'ATIVO' | 'INATIVO'>('ATIVO')

  useEffect(() => {
    if (!form) return
    setName(form.name ?? ''); setDescription(form.description ?? '')
    setSortOrder(form.sortOrder ?? 0); setStatus((form.status as 'ATIVO') ?? 'ATIVO')
  }, [form])

  return (
    <Modal open={!!form} onClose={onClose} title={form?.id ? 'Editar curva' : 'Nova curva de desconto'} size="md"
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button loading={busy} disabled={!name.trim()} onClick={() => onSave({
          id: form?.id, name: name.trim(), description: description.trim() || null, sortOrder: sortOrder ?? 0, status,
        })}>Salvar</Button>
      </>}>
      <div className="space-y-4">
        <Input label="Nome" required value={name} onChange={e => setName(e.target.value)} placeholder="Ex.: Individual" />
        <RichTextEditor compact label="Descrição" value={description} onChange={setDescription} />
        <NumberInput label="Ordem" value={sortOrder} onChange={setSortOrder} min={0} />
        <StatusToggle value={status} onChange={setStatus} />
        <p className="text-xs text-gray-500">
          As faixas de vidas de cada curva ficam na tela da tabela de precificação, porque mudam a cada versão.
        </p>
      </div>
    </Modal>
  )
}
